import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { useCurrentAuthenticatedUser } from 'src/app/shared/hooks/session';
import { getLoginOrJoinMethod, LOGIN_OR_JOIN_METHOD_QUERY_KEY } from 'src/app/shared/utils/loginOrJoin';

import type { SerializedStepState, StepState } from '../../types';
import { useCurrentSteps } from '../useCurrentSteps';
import { useQueryParamState } from '../useQueryParamState';

/**
 * Steps metadata - we will only generate a new key if the steps have changed
 *
 * Steps in the default flow can be toggled on/off through step specific FCM's
 * e.g. a user can start the flow with `ask, description, and phone-collection` and on their return visit
 * if the `phone-collection` fcm is disabled, the new steps would be `ask, description`
 * In this scenario we will want to reset their state, so we store a key of the step names to compare against
 */
function useStateSteps(stepState: SerializedStepState | undefined) {
	const currentSteps = useCurrentSteps();
	const previousStepsKey = stepState?.metadata.stepsKey;
	const currentStepsKey = currentSteps.map((step) => step.name).join(',');
	const didStepsChange = previousStepsKey !== currentStepsKey;
	return { currentSteps, currentStepsKey, didStepsChange };
}

export function useInitialState(stepState: SerializedStepState | undefined): StepState {
	const user = useCurrentAuthenticatedUser();

	/**
	 * User Metadata
	 * id - always keep an reference to the current user id
	 * initialLoginState - which state the user started the flow in
	 * loginOrJoinMethod - initializes after the user authenticates
	 */
	const loginOrJoinMethodShortcode = useQueryParamValue(LOGIN_OR_JOIN_METHOD_QUERY_KEY) ?? '';
	const loginOrJoinMethod = getLoginOrJoinMethod(loginOrJoinMethodShortcode) ?? undefined;
	const getUserState = () => {
		const currentUserState = user?.id ? ('AUTHENTICATED' as const) : ('GUEST' as const);
		return {
			id: user?.id,
			initialLoginState: stepState?.metadata.user.initialLoginState || currentUserState,
			loginOrJoinMethod,
		};
	};

	const defaultMetadata = { currentStepIndex: 0, currentSubStepIndex: 0, direction: 'forward' as const };
	const { currentSteps, currentStepsKey, didStepsChange } = useStateSteps(stepState);

	// if query param exists
	const queryParamState = useQueryParamState(stepState, user);
	if (queryParamState) {
		return {
			...queryParamState,
			metadata: { ...queryParamState.metadata, user: getUserState() },
		};
	}
	// initialize data for first users
	if (!stepState)
		return {
			metadata: {
				...defaultMetadata,
				user: getUserState(),
				steps: currentSteps,
				stepsKey: currentStepsKey,
			},
		};

	// clear petition data if:
	// - current authenticated user id does not match the stored user id
	// - current user steps does not match the stored steps
	const didUserChange = stepState.metadata.user.id && user?.id !== stepState.metadata.user.id;
	if (didUserChange || didStepsChange) {
		return {
			metadata: {
				...defaultMetadata,
				user: getUserState(),
				steps: currentSteps,
				stepsKey: currentStepsKey,
			},
		};
	}

	// else return previous data and recapture user data (for `loginOrJoin` method)
	return {
		...stepState,
		metadata: { ...stepState.metadata, steps: currentSteps, user: getUserState() },
	};
}
